import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { api, call } from '@/api/client'
import type { ApiFailure } from '@/api/client'
import { FullPageLoading } from '@/ui/states'
import { APEX_BRAND, BrandContext, brandFromApi, looksLikeApex } from './context'
import type { Brand, BrandValue } from './context'
import { applyFavicon, faviconFor } from './favicon'

/**
 * The page knows whose it is before anything else renders.
 *
 * One `GET /api/v1/branding` on mount, and nothing underneath it draws until
 * the answer is in. That is deliberate and it is the whole point: the login
 * screen on `acme.augrank.com` must never flash AugRank's bullets and *Start a
 * free trial* before swapping to Acme's logo — the tenant bought a white label,
 * and a white label that shows the manufacturer's name for 300ms is not one.
 * The same request also decides whether the shell is reachable at all
 * (`RequireTenantHost`), which is a routing decision no route can be allowed
 * to make on a guess.
 *
 * Outermost, above `AuthProvider`: the brand does not depend on who is signed
 * in, and the sign-in screens depend on the brand.
 *
 * **On failure it is the apex, and the app still works — on the apex.** See
 * `APEX_BRAND` on why that is the safe direction there. The failure is not
 * surfaced: the page a visitor is about to see has its own error handling for
 * the requests that matter, and a banner about a cosmetic request having
 * failed would be the first thing every user saw on a bad network.
 *
 * **Anywhere else the failure is the page.** On a hostname that is not the
 * apex (`looksLikeApex`) the fallback would dress a tenant's white-labelled
 * host in AugRank's name for as long as the network was bad — the exact leak
 * the wait-before-render above exists to prevent. So a tenant-shaped host
 * that cannot read its brand shows one sentence and a way to try again, and
 * nothing underneath it renders until the brand is known.
 */
export function BrandProvider({
  children,
  initial,
}: {
  children: ReactNode
  /**
   * A brand already known, so nothing waits on the request.
   *
   * The shipped app never passes one — `main.tsx` mounts this bare, and the
   * fetch is the whole point. The test harness does (`renderApp`), with the
   * same answer its `fetch` stub would give, so a suite about a screen's own
   * loading state can assert on it synchronously instead of first waiting
   * out a request that is not the thing under test. `refresh()` fetches
   * regardless.
   */
  initial?: Brand
}): JSX.Element {
  const [brand, setBrand] = useState<Brand | null>(initial ?? null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    const result = await call(() => api.GET('/api/v1/branding', signal ? { signal } : {}))
    if (!mounted.current) return
    if (result.ok) {
      setFailure(null)
      setBrand(brandFromApi(result.data))
      return
    }
    if (looksLikeApex(window.location.hostname)) {
      setFailure(null)
      setBrand(APEX_BRAND)
      return
    }
    setFailure(result.failure)
  }, [])

  const seeded = initial !== undefined
  useEffect(() => {
    if (seeded) return
    const controller = new AbortController()
    void (async () => {
      try {
        await load(controller.signal)
      } catch {
        // `call()` re-throws only AbortError, which is this effect cleaning up.
      }
    })()
    return () => {
      controller.abort()
    }
  }, [load, seeded])

  // The document's own chrome, kept in step with the brand: the tab title and
  // the icon. Set from an effect rather than at load time so a `refresh()` after
  // a logo upload moves the icon too.
  useEffect(() => {
    if (!brand) return
    document.title = brand.productName
    applyFavicon(faviconFor(brand))
  }, [brand])

  const refresh = useCallback(async () => {
    await load()
  }, [load])

  const value = useMemo<BrandValue | null>(
    () => (brand ? { brand, refresh } : null),
    [brand, refresh],
  )

  if (!value) {
    if (failure) return <WorkspaceUnavailable failure={failure} onRetry={refresh} />
    return <FullPageLoading label="Loading" />
  }
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
}

/**
 * A tenant-shaped host whose brand could not be read. Neutral on purpose: no
 * product name, because the one thing this page does not know is whose it is.
 */
function WorkspaceUnavailable({
  failure,
  onRetry,
}: {
  failure: ApiFailure
  onRetry: () => Promise<void>
}): JSX.Element {
  return (
    <div className="app-full-state" role="alert">
      <div className="ar-error-state__icon" aria-hidden="true">
        !
      </div>
      <p className="ar-error-state__title">Could not load this workspace.</p>
      <p className="ar-muted">{failure.message}</p>
      <button
        type="button"
        className="ar-btn ar-btn--primary"
        onClick={() => {
          void onRetry()
        }}
      >
        Reload
      </button>
    </div>
  )
}
